import React, { useCallback, useState } from 'react'
import { DSIHtml, Modal, useJsonApi } from 'shared'
import { DonateButton } from 'ppp'
import classNames from 'classnames'

import wirWunderLogo from '../../../assets/images/logos/wirwunder_logo.svg'
import { MatchingEventProps, MatchingEventModalContentProps, MatchingEvent } from './types'
import { ProjectStatus } from 'shared/hooks/use_project/types'

type MatchingEventNoticeProps = { matchingEvent: MatchingEvent; status: ProjectStatus } & Pick<
  MatchingEventProps,
  'project'
>

export const MatchingEventNotices = ({ matchingEventUrl, project, status }: MatchingEventProps) => {
  const { data: matchingEvents } = useJsonApi<Array<MatchingEvent>>(matchingEventUrl)
  if (!matchingEvents) return null

  return (
    <>
      {matchingEvents.map((matchingEvent, key) => (
        <MatchingEventNotice matchingEvent={matchingEvent} project={project} status={status} key={key} />
      ))}
    </>
  )
}

const MatchingEventNotice = ({ matchingEvent, project, status }: MatchingEventNoticeProps) => {
  const [show, setShow] = useState(false)
  const onHide = useCallback(() => setShow(false), [])

  const handleClick = useCallback(() => {
    setShow(true)
    ;(document.activeElement as HTMLElement | null)?.blur()
  }, [])

  if (!matchingEvent?.providedAmountInCents) return null

  return (
    <div className="mb-4 p-3" style={{ backgroundColor: matchingEvent.backgroundColor, color: matchingEvent.color }}>
      <div className="row">
        <div className="col-8 flex items-center justify-center">
          <img src={matchingEvent.logo || wirWunderLogo} alt="" style={{ maxWidth: '90%', flexShrink: 0 }} />
        </div>
        <div className="col-16 pl-1">
          <p className="mb-0">
            <DSIHtml className="matching-event-banner" value={matchingEvent.bannerContent} />
            <a onClick={handleClick} className="gentle-link block">
              {I18n.t('projects.show.stats.matching_event.banner_read_conditions')}
            </a>
          </p>
        </div>
        <Modal
          show={show}
          onHide={onHide}
          content={<ModalContent matchingEvent={matchingEvent} project={project} status={status} onHide={onHide} />}
          header={matchingEvent.modalHeadline}
        />
      </div>
    </div>
  )
}

const ModalContent = ({
  matchingEvent: {
    backgroundColor,
    logo,
    modalDuration,
    modalBudget,
    modalMaximumAmount,
    modalFurtherInfo,
    branchLogo,
  },
  project,
  onHide,
  status,
}: MatchingEventModalContentProps): JSX.Element => {
  return (
    <>
      <div
        className={classNames('flex text-center text-white items-center', logo ? 'justify-around' : 'justify-between')}
        style={{ backgroundColor }}
      >
        <img
          className={classNames('py-4 flex-grow-0 flex-shrink', !logo && 'ml-4 ')}
          src={logo || wirWunderLogo}
          alt=""
          style={{ maxHeight: '80px', maxWidth: '45%' }}
        />
        {!logo && (
          <img
            className="mr-4 flex-grow-0 flex-shrink"
            src={branchLogo}
            alt=""
            style={{ maxHeight: '80px', maxWidth: '45%' }}
          />
        )}
      </div>
      <div className="my-4">
        <p>
          <DSIHtml value={modalDuration} />
        </p>
        <p>
          <DSIHtml value={modalBudget} />
        </p>
        <p>
          <DSIHtml value={modalMaximumAmount} />
        </p>
        <p className="my-6">
          <DSIHtml value={modalFurtherInfo} />
        </p>
      </div>
      <div className="flex justify-between">
        <button type="button" className="simulated-link" onClick={onHide}>
          {I18n.t('misc.close')}
        </button>
        {!project.donations_prohibited && (
          <DonateButton
            project={project}
            status={status}
            className="btn-large"
            gaAction="click donate_button__matching_event"
          />
        )}
      </div>
    </>
  )
}
